import { useTranslation } from "react-i18next";
import { InvoiceDto } from "@/types/api/api-types";
import { createColumnHelper } from "@tanstack/react-table";
import Pdf from "@/assets/icons/pdf-2.svg?react";
import { InvoicePaymentStatusBadge } from "@/components/shared/badge/invoicePaymentStatus.badge";
import { Dispatch, SetStateAction } from "react";
import { Pen } from "lucide-react";
import { Table } from "lucide-react";
import { FileCog } from "lucide-react";
import { FileUp } from "lucide-react";
import { Trash } from "lucide-react";

const columnHelper = createColumnHelper<InvoiceDto>();

const STATUS_EDIT_ROLES = ["SUPERADMIN", "ACCOUNTANT"];

export const useInvoiceToBeValidatedTableColumns = (
  displayDocument: (invoiceKey: string) => Promise<void>,
  role: string | null,
  setInvoiceToEdit: Dispatch<SetStateAction<InvoiceDto | null>>,
  setInvoiceDeleteModal: Dispatch<SetStateAction<string | null>>,
) => {
  const { t } = useTranslation("boInvoices");
  return [
    columnHelper.display({
      id: "client",
      header: t("toBeValidatedTable.columns.client"),
      cell: (info) => (
        <div className="max-w-[300px] truncate">
          {info.row.original.billingEntity.company.businessName}
        </div>
      ),
    }),
    columnHelper.display({
      id: "invoice",
      header: t("toBeValidatedTable.columns.invoice"),
      cell: (info) => (
        <div className="flex flex-row items-center gap-4">
          {info.row.original.type === "AUTO" ? (
            <FileCog width={20} height={20} />
          ) : (
            <FileUp width={20} height={20} />
          )}
          <span className="max-w-[400px] truncate text-sm font-medium text-gray-900">
            {t("toBeValidatedTable.invoiceName", {
              invoiceId: info.row.original.invoiceId,
              companyName: info.row.original.billingEntity.companyName,
            })}
          </span>
        </div>
      ),
    }),
    columnHelper.display({
      id: "amount",
      header: t("toBeValidatedTable.columns.amount"),
      cell: (info) => (
        <div className="flex flex-row items-center gap-4">
          <span className="text-sm font-medium text-gray-900">
            {(info.row.original.amountToBill / 100).toLocaleString("fr-FR", {
              style: "currency",
              currency: "EUR",
            })}
          </span>
        </div>
      ),
    }),
    columnHelper.display({
      id: "updatedBy",
      header: t("toBeValidatedTable.columns.updatedBy"),
      cell: (info) => {
        if (!info.row.original.updatedBy) return "";
        return `${info.row.original.updatedBy?.firstName.slice(0, 1).toUpperCase()}${info.row.original.updatedBy?.lastName.slice(0, 1).toUpperCase()}`;
      },
    }),
    columnHelper.display({
      id: "status",
      header: t("toBeValidatedTable.columns.status"),
      meta: `justify-center`,
      cell: (info) => <InvoicePaymentStatusBadge status={info.row.original.paymentStatus} />,
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: (info) => (
        <div className="grid w-[170px] grid-cols-5 items-center justify-center gap-3">
          <button
            className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
            onClick={() => displayDocument(info.row.original.invoiceKey)}
          >
            <Table width={20} height={20} color="#06F" />
          </button>
          {info.row.original.invoicePDFKey ? (
            <button
              className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
              onClick={() => displayDocument(info.row.original.invoicePDFKey!)}
            >
              <Pdf width={20} height={20} color="#06F" />
            </button>
          ) : (
            <div />
          )}
          <button
            className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
            onClick={() => setInvoiceToEdit(info.row.original)}
          >
            <Pen width={20} height={20} color="#06F" />
          </button>
          {STATUS_EDIT_ROLES.includes(role || "") && (
            <button
              className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
              onClick={() => setInvoiceDeleteModal(info.row.original.id)}
            >
              <Trash width={20} height={20} color="#ff0000" />
            </button>
          )}
        </div>
      ),
    }),
  ];
};
